import React, { useEffect, useMemo, useState } from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { FadeUp } from '../components/MotionReveals';
import {
  Gauge, AlertTriangle, ClipboardCheck, RotateCcw, Eye, CheckCircle2,
  ChevronRight, Clock, HelpCircle, Quote, ListChecks, Route, Octagon, Triangle, Scale,
} from 'lucide-react';

// ─── Données pédagogiques Stop & Cédez-le-passage ───
const SCENARIOS = {
  stop: {
    tab: 'Le STOP (AB4)',
    icon: Octagon,
    badge: 'Intersection AB4 · Arrêt complet obligatoire · Faute éliminatoire si glissé',
    titre: 'Le STOP — arrêt complet, pas de négociation.',
    scene: 'stop',
    chapters: [
      { label: 'AB5 à 150 m', desc: 'Annonce « STOP à 150 m » hors agglo : on prépare le ralentissement' },
      { label: 'Rétro + dégressif', desc: 'Rétro intérieur, freinage dégressif, rétrogradage jusqu\'à la 1ère' },
      { label: 'Arrêt COMPLET à la ligne', desc: 'Roues immobiles au niveau de la ligne continue, pas après' },
      { label: 'G-D-G puis insertion', desc: 'Contrôle gauche-droite-gauche, on s\'engage franchement en 1ère' },
    ],
    dangerNote: 'Le « stop glissé » (roues jamais totalement arrêtées) est une faute ÉLIMINATOIRE à l\'examen, même à 2 km/h. L\'arrêt doit être net, ressenti, roues immobiles.',
    focus: {
      txt: <>L'erreur n°1 des élèves : s'arrêter <strong className="text-white">trop loin derrière la ligne</strong>, là où on ne voit rien, puis repartir sans refaire un vrai contrôle. Si la visibilité est masquée (haie, véhicule en stationnement), on fait un <strong className="text-white">double arrêt</strong> : un à la ligne (obligation légale), un en position avancée pour voir.</>,
      tags: ['Arrêt roues immobiles = obligatoire', 'Double arrêt si visibilité masquée'],
    },
    quiz: [
      { q: 'Au STOP, la route est totalement dégagée. Que fais-tu ?', opts: ['Je ralentis fortement et je passe sans m\'arrêter', 'Je m\'arrête complètement quand même, puis je repars', 'Je m\'arrête seulement s\'il y a un véhicule'], ok: 1,
        exp: 'L\'arrêt est absolu et inconditionnel : même à 4h du matin dans le désert, roues immobiles à la ligne. Stop glissé = éliminatoire.' },
      { q: 'Où dois-tu immobiliser le véhicule ?', opts: ['Au niveau de la ligne continue d\'arrêt (à son aplomb)', '2-3 mètres avant la ligne, par sécurité', 'Après la ligne, pour mieux voir'], ok: 0,
        exp: 'L\'arrêt se fait à la limite de la ligne (R415-6). Si la visibilité est insuffisante, on refait un second arrêt avancé APRÈS le premier.' },
      { q: 'Visibilité masquée par une haie : quelle procédure ?', opts: ['Je m\'avance directement jusqu\'à voir, sans arrêt à la ligne', 'Arrêt à la ligne, PUIS avancée lente et second arrêt en position de vue', 'Je klaxonne et je passe'], ok: 1,
        exp: 'Le double arrêt : le 1er à la ligne (obligation), le 2ème en « position de bec » pour construire la visibilité. Les deux comptent à l\'examen.' },
      { q: 'Dans quel rapport repars-tu après un STOP ?', opts: ['2ème pour relancer plus vite', '1ère, toujours — on part de 0 km/h', 'Point mort puis 1ère une fois engagé'], ok: 1,
        exp: 'Arrêt complet = départ en 1ère. Repartir en 2ème = risque de calage au milieu de l\'intersection, l\'endroit le plus dangereux.' },
      { q: 'L\'ordre des contrôles avant de s\'engager ?', opts: ['Droite puis gauche', 'Gauche - droite - gauche (le dernier côté d\'où vient le danger le plus proche)', 'Un seul regard panoramique suffit'], ok: 1,
        exp: 'G-D-G : le danger le plus proche vient de gauche (il croise ta trajectoire en premier). Le dernier contrôle revalide ce côté juste avant de t\'engager.' },
      { q: 'Combien de temps dois-tu rester arrêté au STOP ?', opts: ['3 secondes minimum, c\'est la loi', 'Aucune durée légale : roues immobiles + contrôles efficaces suffisent', '1 seconde chrono'], ok: 1,
        exp: 'Mythe des « 3 secondes » : aucune durée dans le Code. Ce qui compte : immobilisation réelle + prise d\'information complète avant insertion.' },
    ],
    curve: [50, 50, 48, 42, 36, 28, 20, 12, 5, 0, 0, 0, 8, 15, 22, 28, 34, 40, 45, 50],
    stats: [{ v: 50, l: 'km/h approche' }, { v: 0, l: 'km/h à la ligne' }, { v: 'G-D-G', l: 'contrôles' }],
    identite: {
      type: 'Intersection AB4 — priorité ponctuelle',
      typeDesc: 'Panneau octogonal STOP + ligne continue d\'arrêt.',
      difficulte: 'Fondamental',
      difficulteDesc: 'Simple en théorie, éliminatoire si bâclé.',
      danger: 'Stop glissé + visibilité masquée',
      dangerDesc: 'Arrêt non marqué ou insertion sans voir.',
      chips: ['🛑 AB4 · Ligne continue · Marquage STOP', '📍 Hors agglo : annoncé par AB5 à 150 m', '⚖️ Art. R415-6 — arrêt absolu'],
    },
    comprendre: <>Le STOP n'est pas un « gros cédez » : c'est un <strong className="text-white">arrêt absolu, roues immobiles</strong>, même si tout est désert. Il est placé là où la visibilité ne permet PAS de juger en roulant. L'arrêt t'offre le temps d'analyse que la route ne te donne pas.</>,
    vitesseLegale: '50 → 0 obligatoire',
    rapport: '2ème → 1ère → arrêt',
    remc: 'Réf : REMC C2 — Franchir une intersection à régime de priorité',
    segments: [
      { time: 'Phase 1', speed: '50 → 36 km/h', title: 'AB5 annonce à 150 m', emoji: '👁️',
        obs: ['Panneau AB5 « STOP à 150 m » (hors agglo) : l\'information arrive AVANT l\'intersection', 'Lecture de l\'environnement : haies, murs, véhicules qui masquent', 'Rétro intérieur : qui me suit, à quelle distance ?', 'Lever de pied anticipé, pas de freinage tardif'],
        q: 'Pourquoi un panneau d\'annonce 150 m avant ?',
        r: 'Parce qu\'à 80 km/h il faut ~130 m pour s\'arrêter confortablement. L\'AB5 transforme un arrêt d\'urgence en arrêt préparé. En agglo, pas d\'annonce : le AB4 seul suffit.',
        verdict: 'Vitesse déjà réduite de 30 % avant même de voir la ligne.' },
      { time: 'Phase 2', speed: '36 → 12 km/h', title: 'Freinage dégressif', emoji: '🐢',
        obs: ['Freinage dégressif : fort au début, doux à la fin (pas l\'inverse)', 'Rétrogradage : 3ème → 2ème, débrayage en fin de freinage', 'Déjà un pré-regard gauche-droite pendant l\'approche', 'Position : rester dans sa voie, ne pas serrer à droite exagérément'],
        q: 'Je débraye quand exactement ?',
        r: 'En dessous de ~20 km/h, sinon le frein moteur disparaît trop tôt et le freinage s\'allonge. Débrayer trop tôt = « roue libre » sanctionnée.',
        verdict: 'Freinage lisible pour celui qui suit, aucun à-coup.' },
      { time: 'Phase 3', speed: '0 km/h', title: 'Arrêt complet à la ligne', emoji: '🛑',
        obs: ['Roues totalement immobiles — le véhicule « s\'assoit » sur ses suspensions', 'Positionné à l\'aplomb de la ligne continue, pas 3 m avant', '1ère enclenchée pendant l\'arrêt, frein maintenu', 'Contrôles G-D-G amples, tête qui tourne vraiment'],
        q: 'Et si je ne vois rien depuis la ligne ?',
        r: 'Double arrêt : arrêt n°1 à la ligne (la loi), puis avancée au pas et arrêt n°2 en position de vue (la sécurité). L\'examinateur attend exactement cette procédure.',
        verdict: 'L\'arrêt doit être RESSENTI. En cas de doute, il n\'a pas eu lieu.' },
      { time: 'Phase 4', speed: '0 → 30 km/h', title: 'Insertion franche', emoji: '💨',
        obs: ['Dernier contrôle gauche juste avant de lâcher le frein', 'Démarrage franc en 1ère : on traverse, on ne flâne pas dans l\'intersection', 'Passage 2ème une fois la trajectoire stabilisée', 'Re-contrôle rétro après insertion'],
        q: 'Un véhicule arrive au loin : je passe ou j\'attends ?',
        r: 'Règle du créneau : il te faut le temps de traverser + une marge. Si tu obliges l\'autre à ralentir, c\'est un refus de priorité. Dans le doute, on attend.',
        verdict: 'Décision nette : soit on attend, soit on traverse franchement. Jamais entre les deux.' },
    ],
    pieges: [
      { t: 'Le stop « glissé »', d: 'Rouler à 2 km/h sans jamais immobiliser les roues. Éliminatoire à l\'examen, infraction à 4 points sur route.', tag: 'Éliminatoire' },
      { t: 'Arrêt trop en retrait', d: 'S\'arrêter 3 m avant la ligne « par prudence » : tu ne vois rien et tu bloques ta propre prise d\'info. La ligne est placée au point de vue optimal légal.', tag: 'Visibilité' },
      { t: 'Insertion molle', d: 'Repartir en sous-régime ou hésiter au milieu du carrefour. L\'intersection est la zone la plus exposée : on la traverse franchement.', tag: 'Allure' },
    ],
    conduiteModele: <>Le conducteur modèle marque un arrêt <strong className="text-white">net et ressenti</strong> à l'aplomb de la ligne, enclenche la 1ère pendant l'arrêt, balaye G-D-G avec la tête, puis traverse franchement sans stationner dans l'intersection.</>,
    grille: [
      { l: 'Panneau AB5/AB4 détecté et verbalisé tôt', c: 'Prise d\'info lointaine' },
      { l: 'Rétro intérieur avant de ralentir', c: 'Contrôles' },
      { l: 'Freinage dégressif sans à-coup', c: 'Gestion allure' },
      { l: 'Rétrogradage jusqu\'à la 1ère', c: 'Gestion boîte' },
      { l: 'Arrêt COMPLET roues immobiles', c: 'Règle absolue' },
      { l: 'Arrêt positionné à l\'aplomb de la ligne', c: 'Positionnement' },
      { l: 'Double arrêt si visibilité masquée', c: 'Méthode' },
      { l: 'Contrôles G-D-G amples (tête)', c: 'Observation' },
      { l: 'Dernier regard à gauche avant départ', c: 'Contrôles' },
      { l: 'Insertion franche sans hésitation', c: 'Allure sortie' },
      { l: 'Pas de calage (1ère + point de patinage)', c: 'Maîtrise' },
    ],
    phrase: '« Un stop, ça se sent dans le siège : si tu doutes de t\'être arrêté, tu ne t\'es pas arrêté. »',
  },
  cedez: {
    tab: 'Le Cédez-le-passage (AB3a)',
    icon: Triangle,
    badge: 'Intersection AB3a · Céder sans arrêt obligatoire · Le roulement est valorisé',
    titre: 'Le Cédez — on cède, mais on ne s\'arrête pas pour rien.',
    scene: 'cedez',
    chapters: [
      { label: 'AB3b à 150 m', desc: 'Annonce hors agglo : on prépare une approche « décision »' },
      { label: 'Allure d\'observation', desc: 'On descend vers 20-30 km/h, assez lent pour analyser, assez rapide pour s\'insérer' },
      { label: 'Décision à la ligne pointillée', desc: 'Libre → on garde le roulement. Occupé → on cède, arrêt seulement si nécessaire' },
      { label: 'Insertion dynamique', desc: 'On accélère franchement pour se fondre dans le flux' },
    ],
    dangerNote: 'S\'arrêter systématiquement à un cédez LIBRE n\'est pas « prudent » : c\'est une gêne pour ceux qui suivent, un risque de collision arrière et une perte de fluidité sanctionnée à l\'examen.',
    focus: {
      txt: <>Le cédez est un <strong className="text-white">exercice de décision</strong>, pas d'arrêt. L'élève doit arriver à une allure qui lui laisse DEUX options ouvertes : continuer si c'est libre, s'arrêter si c'est occupé. Arriver trop vite supprime l'option « s'arrêter » ; arriver trop lentement (ou s'arrêter pour rien) supprime la fluidité. La bonne allure, c'est <strong className="text-white">20-30 km/h en 2ème</strong>.</>,
      tags: ['Garder le roulement si libre', 'Allure = pouvoir s\'arrêter OU continuer'],
    },
    quiz: [
      { q: 'Au cédez-le-passage, la voie est libre. Que fais-tu ?', opts: ['Je m\'arrête quand même, comme au STOP', 'Je m\'insère sans m\'arrêter, en gardant une allure d\'observation', 'J\'accélère pour passer avant tout le monde'], ok: 1,
        exp: 'Le cédez n\'impose l\'arrêt QUE si un usager prioritaire arrive. Voie libre = on garde le roulement, c\'est même valorisé à l\'examen (fluidité).' },
      { q: 'Quelle est LA différence entre STOP et cédez ?', opts: ['Au cédez on est prioritaire', 'Le STOP impose l\'arrêt absolu, le cédez impose seulement de laisser passer', 'Il n\'y en a pas, c\'est pareil'], ok: 1,
        exp: 'Même obligation de céder la priorité, mais le STOP ajoute l\'arrêt obligatoire inconditionnel. Au cédez, l\'arrêt n\'est qu\'un moyen, pas une obligation.' },
      { q: 'Le marquage au sol d\'un cédez-le-passage ?', opts: ['Une ligne continue', 'Une ligne de triangles blancs pointe vers toi + ligne discontinue', 'Un damier rouge et blanc'], ok: 1,
        exp: 'Triangles blancs « dents de requin » pointés vers le véhicule qui doit céder + ligne pointillée (T2). La ligne continue, c\'est le STOP.' },
      { q: 'Tu arrives au cédez à 45 km/h « pour être fluide ». Correct ?', opts: ['Oui, la fluidité est valorisée', 'Non : à cette allure je ne peux plus m\'arrêter si quelqu\'un surgit, je n\'ai plus qu\'une option', 'Oui si je klaxonne'], ok: 1,
        exp: 'La bonne allure doit préserver les DEUX options (passer ou s\'arrêter). À 45 km/h, tu ne peux plus céder : c\'est un refus de priorité en puissance.' },
      { q: 'Un vélo arrive sur la voie prioritaire. Tu…', opts: ['Passes vite, un vélo c\'est lent', 'Lui cèdes le passage comme à n\'importe quel véhicule', 'Klaxonnes pour le prévenir'], ok: 1,
        exp: 'Le cédez protège TOUS les usagers de la voie prioritaire : vélos, motos, bus. Forcer le passage devant un vulnérable = éliminatoire + mise en danger.' },
      { q: 'Au cédez, flux dense et continu : que fais-tu ?', opts: ['Je force doucement pour créer un passage', 'Je m\'arrête à la ligne et j\'attends un vrai créneau', 'Je passe par l\'accotement'], ok: 1,
        exp: 'Flux occupé = on cède jusqu\'à l\'arrêt complet si nécessaire. Savoir attendre un créneau suffisant fait partie de la compétence « s\'insérer ».' },
    ],
    curve: [50, 50, 47, 42, 36, 31, 27, 24, 22, 20, 22, 26, 31, 36, 41, 45, 48, 50, 50, 50],
    stats: [{ v: 50, l: 'km/h approche' }, { v: 20, l: 'km/h décision' }, { v: '2ème', l: 'rapport tenu' }],
    identite: {
      type: 'Intersection AB3a — priorité ponctuelle',
      typeDesc: 'Triangle inversé + dents de requin au sol.',
      difficulte: 'Intermédiaire',
      difficulteDesc: 'Demande une vraie prise de décision.',
      danger: 'Mauvaise allure d\'approche',
      dangerDesc: 'Trop vite = refus de priorité. Arrêt inutile = collision arrière.',
      chips: ['🔻 AB3a · Dents de requin · Ligne T2', '📍 Hors agglo : annoncé par AB3b à 150 m', '⚖️ Art. R415-7 — céder, arrêt si nécessaire'],
    },
    comprendre: <>Le cédez-le-passage teste ta capacité à <strong className="text-white">décider en roulant</strong>. Contrairement au STOP, personne ne décide pour toi : c'est TOI qui évalues si le créneau est suffisant. La compétence travaillée, c'est la prise d'information dynamique — celle qui servira ensuite aux giratoires et aux insertions d'autoroute.</>,
    vitesseLegale: '50 → 20-30 décision',
    rapport: '3ème → 2ème tenue',
    remc: 'Réf : REMC C2 — Franchir une intersection à régime de priorité',
    segments: [
      { time: 'Phase 1', speed: '50 → 36 km/h', title: 'AB3b annonce', emoji: '👁️',
        obs: ['Panneau AB3b « cédez à 150 m » hors agglo', 'Lecture précoce du flux prioritaire : d\'où viennent-ils, à quelle cadence ?', 'Rétro intérieur : informer celui qui suit par un ralentissement progressif', 'Chercher déjà le futur créneau du regard'],
        q: 'Qu\'est-ce que je cherche du regard à 150 m ?',
        r: 'Le RYTHME du flux prioritaire : paquets de véhicules ou trafic clairsemé ? C\'est lui qui déterminera si tu passeras en roulant ou si tu devras t\'arrêter.',
        verdict: 'À 150 m, le plan A (passer) et le plan B (céder) sont déjà construits.' },
      { time: 'Phase 2', speed: '36 → 22 km/h', title: 'Allure de décision', emoji: '⚖️',
        obs: ['Descente vers 20-30 km/h : l\'allure qui garde les 2 options ouvertes', '2ème tenue, pied prêt sur le frein, PAS débrayé en continu', 'Balayage visuel ample gauche-droite, tête mobile', 'Position : axe de sa voie, ne pas « plonger » vers la ligne'],
        q: 'Pourquoi ne pas débrayer longtemps ?',
        r: 'Débrayé = roue libre = aucune réponse moteur si tu dois accélérer pour t\'insérer. On garde la 2ème engagée pour rester « vivant » mécaniquement.',
        verdict: '22 km/h à 20 m de la ligne : ni trop vite, ni mourant.' },
      { time: 'Phase 3', speed: '20 km/h ou 0', title: 'La décision', emoji: '🎯',
        obs: ['Libre → on maintient 20 km/h et on franchit sans s\'arrêter', 'Occupé → on finit de freiner, arrêt à hauteur des dents de requin', 'Jamais de demi-décision : l\'hésitation au milieu est le pire scénario', 'Si arrêt : 1ère, et on recommence la recherche de créneau'],
        q: 'Comment je sais si le créneau suffit ?',
        r: 'Règle simple : si l\'autre doit toucher son frein à cause de toi, le créneau était trop court. Compte le temps : il te faut ~4-5 s pour t\'insérer et atteindre l\'allure du flux.',
        verdict: 'Une décision nette prise AVANT la ligne, jamais dessus.' },
      { time: 'Phase 4', speed: '20 → 50 km/h', title: 'Insertion dynamique', emoji: '💨',
        obs: ['Accélération franche pour se fondre dans l\'allure du flux', 'Passage 3ème rapide, pas de sur-régime en 2ème', 'Rétro après insertion : le véhicule derrière a-t-il dû ralentir ?', 'Repositionnement dans l\'axe de la nouvelle voie'],
        q: 'J\'ai hésité et l\'autre a freiné. C\'est grave ?',
        r: 'À l\'examen c\'est un refus de priorité (éliminatoire si l\'autre freine fortement). La leçon : la décision devait être prise plus tôt, à la phase 2.',
        verdict: 'Insertion réussie = personne n\'a modifié son allure à cause de toi.' },
    ],
    pieges: [
      { t: 'L\'arrêt réflexe inutile', d: 'S\'arrêter à un cédez libre « par sécurité ». Résultat : gêne, risque de choc arrière, et insertion à 0 km/h bien plus dangereuse qu\'en roulant.', tag: 'Fluidité' },
      { t: 'L\'approche trop rapide', d: 'Arriver à 40+ km/h en espérant que ce soit libre. Si ça ne l\'est pas, impossible de céder : refus de priorité quasi garanti.', tag: 'Allure' },
      { t: 'Le deux-roues oublié', d: 'On scanne les voitures et on rate la moto ou le vélo, plus fins et plus rapides qu\'estimé. Un contrôle = TOUS les usagers.', tag: 'Vulnérable' },
    ],
    conduiteModele: <>Le conducteur modèle arrive à <strong className="text-white">allure de décision</strong> (20-30 km/h, 2ème tenue), choisit AVANT la ligne, franchit en roulant si c'est libre et accélère franchement pour se fondre dans le flux — personne n'a touché son frein à cause de lui.</>,
    grille: [
      { l: 'Panneau AB3b/AB3a détecté et verbalisé', c: 'Prise d\'info lointaine' },
      { l: 'Rétro intérieur avant ralentissement', c: 'Contrôles' },
      { l: 'Allure de décision 20-30 km/h atteinte', c: 'Gestion allure' },
      { l: '2ème tenue, pas de roue libre débrayée', c: 'Gestion boîte' },
      { l: 'Balayage G-D ample pendant l\'approche', c: 'Observation' },
      { l: 'Décision prise AVANT la ligne', c: 'Décision' },
      { l: 'Roulement conservé si voie libre', c: 'Fluidité' },
      { l: 'Arrêt complet si flux occupé', c: 'Règle' },
      { l: 'Vulnérables (vélo/moto) intégrés au contrôle', c: 'Vulnérable' },
      { l: 'Insertion sans faire freiner personne', c: 'Priorité' },
      { l: 'Accélération franche après insertion', c: 'Allure sortie' },
    ],
    phrase: '« Au cédez, la bonne allure c\'est celle qui te laisse les deux choix : passer, ou t\'arrêter. »',
  },
};

const METHODE = [
  { k: 'R — Repérer', d: 'Panneau d\'annonce + type de ligne au sol' },
  { k: 'A — Adapter', d: 'Allure : 0 absolu au stop, 20-30 au cédez' },
  { k: 'C — Contrôler', d: 'G-D-G amples, vulnérables inclus' },
  { k: 'D — Décider', d: 'Franchement : passer ou attendre, jamais entre les deux' },
];

const CHECKLIST = [
  { t: 'Lecture', d: 'panneau d\'annonce (AB5 / AB3b) verbalisé dès 150 m hors agglo.' },
  { t: 'STOP', d: 'arrêt roues immobiles à l\'aplomb de la ligne, 1ère, G-D-G.' },
  { t: 'Cédez', d: 'allure de décision 20-30 km/h, roulement conservé si libre.' },
  { t: 'Visibilité', d: 'double arrêt (stop) ou avancée prudente si champ masqué.' },
  { t: 'Créneau', d: 'personne ne doit freiner à cause de toi, sinon trop court.' },
  { t: 'Insertion', d: 'franche et accélérée — on ne stationne pas dans une intersection.' },
];

const COMPARATIF = [
  { k: 'Panneau', stop: 'Octogone rouge AB4', cedez: 'Triangle inversé AB3a' },
  { k: 'Marquage sol', stop: 'Ligne continue + « STOP »', cedez: 'Dents de requin + ligne T2' },
  { k: 'Arrêt', stop: 'OBLIGATOIRE, absolu, roues immobiles', cedez: 'Seulement si un prioritaire arrive' },
  { k: 'Rapport de boîte', stop: '1ère (départ de 0)', cedez: '2ème tenue (20-30 km/h)' },
  { k: 'À l\'examen', stop: 'Stop glissé = éliminatoire', cedez: 'Arrêt inutile = fluidité sanctionnée' },
  { k: 'Compétence travaillée', stop: 'Rigueur de procédure', cedez: 'Prise de décision en roulant' },
];

const CHECKLIST_ELEVE = [
  'Je distingue AB4 (stop) et AB3a (cédez) sans hésiter',
  'Au stop : roues immobiles à la ligne, 1ère, G-D-G',
  'Au cédez : j\'arrive à 20-30 km/h, prêt à tout',
  'Je garde le roulement quand le cédez est libre',
  'Je refuse le créneau si l\'autre devrait freiner',
];

// ─── Schéma interactif SVG : intersection vue du dessus ───
function SceneInteractive({ scenario, step, onStep }) {
  const data = SCENARIOS[scenario];
  // position voiture selon l'étape (vue de dessus, la voiture monte vers l'intersection)
  const carY = scenario === 'stop' ? [300, 250, 196, 118][step] : [300, 248, 200, 118][step];
  const carX = step === 3 ? 250 : 215;
  const showCones = step >= 2;
  const atLine = step === 2;
  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5" data-testid="stopcedez-scene">
      <div className="text-white/45 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5">
        <Route size={13} className="text-red-400" /> Schéma interactif · {data.tab}
      </div>
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="rounded-xl overflow-hidden bg-[#121318] border border-white/10 relative">
          <svg viewBox="0 0 440 340" className="w-full" style={{ maxHeight: 380 }} data-testid="stopcedez-svg">
            {/* herbe */}
            <rect x="0" y="0" width="440" height="340" fill="#15241a" />
            {/* route horizontale prioritaire */}
            <rect x="0" y="80" width="440" height="80" fill="#2a2d33" />
            <line x1="0" y1="120" x2="440" y2="120" stroke="#e8e8e8" strokeWidth="2.5" strokeDasharray="18 14" opacity="0.6" />
            {/* route verticale (nous) */}
            <rect x="185" y="80" width="90" height="260" fill="#2a2d33" />
            <line x1="230" y1="170" x2="230" y2="340" stroke="#e8e8e8" strokeWidth="2" strokeDasharray="12 10" opacity="0.45" />
            {/* ligne stop ou cédez */}
            {scenario === 'stop' ? (
              <>
                <rect x="232" y="164" width="40" height="6" fill="#fff" />
                <text x="252" y="192" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="900" letterSpacing="2">STOP</text>
                {/* panneau stop */}
                <g transform="translate(290, 150)">
                  <line x1="0" y1="0" x2="0" y2="26" stroke="#888" strokeWidth="2.5" />
                  <polygon points="0,-20 10,-16 14,-6 14,4 10,14 0,18 -10,14 -14,4 -14,-6 -10,-16" fill="#d22" stroke="#fff" strokeWidth="2" transform="scale(0.9)" />
                  <text x="0" y="2" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="900">STOP</text>
                </g>
              </>
            ) : (
              <>
                {[0, 1, 2].map((i) => (
                  <polygon key={i} points={`${236 + i * 14},164 ${244 + i * 14},164 ${240 + i * 14},176`} fill="#fff" />
                ))}
                <g transform="translate(292, 148)">
                  <line x1="0" y1="0" x2="0" y2="28" stroke="#888" strokeWidth="2.5" />
                  <polygon points="-15,-18 15,-18 0,6" fill="#fff" stroke="#d22" strokeWidth="4" />
                </g>
              </>
            )}
            {/* trafic prioritaire */}
            <g opacity={step >= 1 ? 1 : 0.35} style={{ transition: 'opacity .4s' }}>
              <rect x={step >= 2 ? 60 : 130} y="92" width="34" height="18" rx="4" fill="#5b7fd4" style={{ transition: 'x .8s ease' }} />
              <rect x={step >= 2 ? 330 : 280} y="128" width="34" height="18" rx="4" fill="#c8c8c8" style={{ transition: 'x .8s ease' }} />
            </g>
            {/* cônes de vision */}
            {showCones && (
              <g opacity="0.9">
                <polygon points={`230,${carY + 8} 30,96 30,150`} fill="rgba(255,196,0,0.13)" stroke="rgba(255,196,0,0.45)" strokeWidth="1" strokeDasharray="4 4" />
                <polygon points={`230,${carY + 8} 430,96 430,150`} fill="rgba(255,196,0,0.13)" stroke="rgba(255,196,0,0.45)" strokeWidth="1" strokeDasharray="4 4" />
              </g>
            )}
            {/* notre voiture */}
            <g style={{ transform: `translate(${carX}px, ${carY}px)`, transition: 'transform .8s cubic-bezier(.4,0,.2,1)' }}>
              <rect x="0" y="0" width="30" height="52" rx="7" fill="#dc2626" stroke="#fff" strokeWidth="1.5" transform={step === 3 ? 'rotate(-20 15 26)' : ''} style={{ transition: 'transform .8s' }} />
              <rect x="5" y="8" width="20" height="10" rx="2" fill="#1a1a1e" transform={step === 3 ? 'rotate(-20 15 26)' : ''} style={{ transition: 'transform .8s' }} />
            </g>
            {/* badge vitesse */}
            <g transform="translate(20, 300)">
              <rect x="0" y="-18" width="96" height="30" rx="8" fill="#000" opacity="0.65" />
              <text x="48" y="2" textAnchor="middle" fill={atLine && scenario === 'stop' ? '#f87171' : '#fbbf24'} fontSize="13" fontWeight="900" fontFamily="monospace">
                {scenario === 'stop' ? ['~40 km/h', '~20 km/h', '0 km/h ✓', '1ère →'][step] : ['~40 km/h', '~25 km/h', '20 km/h', '2ème ↗'][step]}
              </text>
            </g>
          </svg>
        </div>
        <div className="space-y-2">
          <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold">4 étapes cliquables</div>
          {data.chapters.map((c, i) => (
            <button key={i} type="button" onClick={() => onStep(i)} data-testid={`stopcedez-step-${i}`}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition ${step === i ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
              <span className="w-8 shrink-0 text-center font-mono-spec text-[11px] font-bold text-red-300 bg-black/40 border border-white/10 rounded-md py-1">{i + 1}</span>
              <span className="flex-1">
                <span className="block text-white/90 text-[12.5px] font-bold leading-tight">{c.label}</span>
                <span className="block text-white/45 text-[11px] mt-0.5 leading-tight">{c.desc}</span>
              </span>
            </button>
          ))}
          <div className="rounded-xl bg-amber-950/20 border border-amber-700/30 p-3 mt-2">
            <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1 inline-flex items-center gap-1.5"><AlertTriangle size={11} /> Danger</div>
            <p className="text-white/70 text-[11.5px] leading-snug">{data.dangerNote}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpeedCurve({ curve }) {
  const max = Math.max(...curve, 1);
  return (
    <div>
      <div className="flex items-end gap-[3px] h-28" aria-hidden="true">
        {curve.map((v, i) => (
          <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-red-700 to-red-400 transition-all" style={{ height: `${Math.max(4, (v / max) * 100)}%`, opacity: 0.55 + 0.45 * (v / max) }} title={`${v} km/h`} />
        ))}
      </div>
      <div className="flex justify-between text-white/40 font-mono-spec text-[10px] mt-1.5">
        <span>approche</span><span>ligne</span><span>décision</span><span>insertion</span>
      </div>
    </div>
  );
}

function Quiz({ questions, storageKey }) {
  const [answers, setAnswers] = useState({});
  useEffect(() => { setAnswers({}); }, [storageKey]);
  const answered = Object.keys(answers).length;
  const score = questions.filter((qu, qi) => answers[qi] === qu.ok).length;
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="stopcedez-quiz">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
          <HelpCircle size={18} className="text-amber-400" /> QCM — valide tes règles
        </h3>
        <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${answered === questions.length ? (score >= 5 ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-amber-950/30 border-amber-600/40 text-amber-300') : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="stopcedez-quiz-score">
          {answered === questions.length ? `${score}/${questions.length} bonnes réponses` : `${answered}/${questions.length} répondues`}
        </span>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {questions.map((qu, qi) => {
          const picked = answers[qi];
          return (
            <div key={qi} className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="text-white text-[13px] font-bold leading-snug">Q{qi + 1} : {qu.q}</div>
                <span className="shrink-0 w-6 h-6 rounded-md bg-black/40 border border-white/10 text-white/50 font-mono-spec text-[10px] font-bold flex items-center justify-center">0{qi + 1}</span>
              </div>
              <div className="space-y-1.5">
                {qu.opts.map((o, oi) => {
                  const isPicked = picked === oi;
                  const showState = picked !== undefined;
                  const good = oi === qu.ok;
                  return (
                    <button key={oi} type="button" disabled={showState} data-testid={`stopcedez-quiz-q${qi}-o${oi}`}
                      onClick={() => setAnswers({ ...answers, [qi]: oi })}
                      className={`w-full flex items-start gap-2 rounded-lg border px-2.5 py-2 text-left text-[12px] leading-snug transition ${
                        showState && good ? 'border-emerald-600/50 bg-emerald-950/30 text-emerald-200'
                        : showState && isPicked ? 'border-red-600/50 bg-red-950/30 text-red-300'
                        : showState ? 'border-white/5 bg-transparent text-white/35'
                        : 'border-white/10 bg-white/[0.02] text-white/75 hover:bg-white/[0.06]'}`}>
                      <span className="shrink-0 w-[18px] h-[18px] rounded-full border border-current flex items-center justify-center text-[9.5px] font-bold">{String.fromCharCode(65 + oi)}</span>
                      {o}
                    </button>
                  );
                })}
              </div>
              {picked !== undefined && (
                <p className={`mt-2.5 text-[11.5px] leading-snug ${picked === qu.ok ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {picked === qu.ok ? '✓ Bonne réponse. ' : '✗ Pas tout à fait. '}{qu.exp}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChecklistEleve() {
  const KEY = 'cv-stopcedez-checklist-eleve';
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; }
  });
  const n = CHECKLIST_ELEVE.filter((_, i) => done[i]).length;
  const toggle = (i) => {
    const next = { ...done, [i]: !done[i] };
    setDone(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };
  return (
    <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" data-testid="stopcedez-checklist-eleve">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
          <CheckCircle2 size={18} className="text-emerald-400" /> Check-list élève — avant ta prochaine leçon
        </h3>
        <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${n === CHECKLIST_ELEVE.length ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="stopcedez-checklist-score">{n}/{CHECKLIST_ELEVE.length} validés</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {CHECKLIST_ELEVE.map((c, i) => (
          <button key={i} type="button" onClick={() => toggle(i)} data-testid={`stopcedez-checklist-${i}`}
            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${done[i] ? 'border-emerald-600/40 bg-emerald-950/20' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${done[i] ? 'bg-emerald-500 border-emerald-500' : 'border-white/25'}`}>
              {done[i] && <CheckCircle2 size={14} className="text-black" />}
            </span>
            <span className={`text-[13px] leading-snug ${done[i] ? 'text-white' : 'text-white/70'}`}>{c}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function StopCedezPage() {
  const [scen, setScen] = useState('stop');
  const [step, setStep] = useState(0);
  const [seg, setSeg] = useState(0);
  const [checked, setChecked] = useState({});
  const [showCorrection, setShowCorrection] = useState(false);
  const data = SCENARIOS[scen];
  const lsKey = `cv-stopcedez-grille-${scen}`;

  useEffect(() => {
    try { setChecked(JSON.parse(localStorage.getItem(lsKey)) || {}); } catch { setChecked({}); }
    setSeg(0);
    setStep(0);
  }, [lsKey]);

  const nbChecked = useMemo(() => data.grille.filter((_, i) => checked[i]).length, [checked, data]);

  const toggle = (i) => {
    const next = { ...checked, [i]: !checked[i] };
    setChecked(next);
    localStorage.setItem(lsKey, JSON.stringify(next));
  };
  const reset = () => { setChecked({}); localStorage.removeItem(lsKey); };

  const s = data.segments[seg];

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12 pb-16" data-testid="stop-cedez-page">
        <FadeUp>
          <SectionTitle
            kicker="Cours interactif · REMC C2"
            title="Stop & Cédez-le-passage, deux règles, deux réflexes."
            sub="Un cours interactif construit par un moniteur ECSR : schéma animé étape par étape, timeline pédagogique, pièges d'examen, QCM et grille de révision à cocher."
          />
        </FadeUp>

        {/* Onglets scénarios */}
        <div className="mt-6 flex flex-wrap gap-2">
          {Object.entries(SCENARIOS).map(([key, v]) => {
            const Icon = v.icon;
            return (
              <button key={key} type="button" onClick={() => setScen(key)} data-testid={`stopcedez-tab-${key}`}
                className={`h-11 px-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 border transition ${
                  scen === key ? 'bg-red-600 border-red-500 text-white shadow-lg shadow-red-900/40' : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06]'
                }`}>
                <Icon size={14} /> {v.tab}
              </button>
            );
          })}
        </div>

        {/* Schéma interactif */}
        <SceneInteractive scenario={scen} step={step} onStep={setStep} />

        {/* Hero fiche */}
        <div className="mt-5 grid lg:grid-cols-[1.5fr_1fr] gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10.5px] font-mono-spec uppercase tracking-widest font-bold mb-3">
              <Clock size={11} /> {data.badge}
            </div>
            <h2 className="font-serif-editorial text-white text-[24px] sm:text-[32px] leading-tight">{data.titre}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {data.identite.chips.map((c, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/70 text-[11.5px]">{c}</span>
              ))}
            </div>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {[
                { k: 'Type d\'intersection', v: data.identite.type, d: data.identite.typeDesc, cl: 'border-white/10' },
                { k: 'Difficulté', v: data.identite.difficulte, d: data.identite.difficulteDesc, cl: 'border-amber-600/30' },
                { k: 'Danger principal', v: data.identite.danger, d: data.identite.dangerDesc, cl: 'border-red-700/40' },
              ].map((c, i) => (
                <div key={i} className={`rounded-xl bg-white/[0.03] border ${c.cl} p-3.5`}>
                  <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold">{c.k}</div>
                  <div className="text-white text-[13.5px] font-bold mt-1 leading-snug">{c.v}</div>
                  <div className="text-white/50 text-[11.5px] mt-1 leading-snug">{c.d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-red-900/40 bg-gradient-to-br from-[#1a0b0d] to-[#0d0d0e] p-5 sm:p-6 flex flex-col">
            <div className="text-white/45 text-[10.5px] uppercase tracking-[0.2em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><Gauge size={12} className="text-red-400" /> Courbe d'allure type</div>
            <SpeedCurve curve={data.curve} />
            <div className="mt-4 grid grid-cols-3 gap-2">
              {data.stats.map((st, i) => (
                <div key={i} className="rounded-lg bg-black/30 border border-white/10 p-2.5 text-center">
                  <div className="font-serif-editorial text-red-400 text-[22px] leading-none">{st.v}</div>
                  <div className="text-white/50 text-[9.5px] uppercase tracking-wider font-mono-spec mt-1">{st.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-xl bg-white/[0.03] border border-white/10 p-3.5 text-[12.5px] text-white/75 leading-relaxed">
              <div className="text-red-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1.5">Ce que doit comprendre l'élève</div>
              {data.comprendre}
              <div className="mt-2.5 flex gap-2">
                <span className="px-2 py-1 rounded bg-black/30 border border-white/10 font-mono-spec text-[10.5px] text-white/60">Allure {data.vitesseLegale}</span>
                <span className="px-2 py-1 rounded bg-black/30 border border-white/10 font-mono-spec text-[10.5px] text-white/60">{data.rapport}</span>
              </div>
              <div className="text-white/35 font-mono-spec text-[10px] mt-2.5">{data.remc}</div>
            </div>
          </div>
        </div>

        {/* Focus pédagogique */}
        <div className="mt-5 rounded-2xl border border-white/15 bg-gradient-to-r from-[#15151a] to-[#0d0d0e] p-4 sm:p-5 flex items-start gap-3" data-testid="stopcedez-focus">
          <span className="shrink-0 w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 flex items-center justify-center"><AlertTriangle size={16} /></span>
          <div>
            <p className="text-white/80 text-[13px] leading-relaxed"><strong className="text-amber-300">Focus pédagogique :</strong> {data.focus.txt}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {data.focus.tags.map((t, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/60 text-[10.5px] font-semibold">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline interactive */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px]">Timeline pédagogique interactive</h3>
            <span className="px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10.5px] font-mono-spec font-bold">4 phases cliquables</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {data.segments.map((sg, i) => (
              <button key={i} type="button" onClick={() => setSeg(i)} data-testid={`stopcedez-seg-${i}`}
                className={`rounded-xl border p-3 text-left transition ${seg === i ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'}`}>
                <div className="font-mono-spec text-[10.5px] text-white/50">{sg.time}</div>
                <div className={`text-[13px] font-bold mt-1 leading-tight ${seg === i ? 'text-red-300' : 'text-white/85'}`}>{sg.emoji} {sg.title}</div>
                <div className="font-mono-spec text-[10.5px] text-white/45 mt-1">{sg.speed}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 grid lg:grid-cols-[1.3fr_1fr] gap-4">
            <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 sm:p-5">
              <div className="text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><Eye size={12} className="text-red-400" /> Ce qu'on observe · {s.time}</div>
              <ul className="space-y-2">
                {s.obs.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-[13px] leading-snug">
                    <ChevronRight size={14} className="text-red-400 mt-0.5 shrink-0" /> {o}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-3">
              <div className="rounded-xl bg-amber-950/15 border border-amber-700/30 p-4">
                <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1.5 inline-flex items-center gap-1.5"><HelpCircle size={12} /> Question moniteur</div>
                <div className="text-white text-[13.5px] font-bold">{s.q}</div>
                <p className="text-white/70 text-[12.5px] mt-2 leading-relaxed">{s.r}</p>
              </div>
              <div className="rounded-xl bg-emerald-950/15 border border-emerald-700/30 p-3.5">
                <div className="text-emerald-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1">Verdict allure</div>
                <p className="text-white/80 text-[12.5px] leading-snug">{s.verdict}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pièges */}
        <div className="mt-8">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] mb-4 inline-flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" /> Les 3 pièges d'examen</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.pieges.map((p, i) => (
              <div key={i} className="rounded-xl border border-red-900/40 bg-[#120a0b] p-4">
                <div className="w-7 h-7 rounded-lg bg-red-600 text-white font-mono-spec text-[12px] font-bold flex items-center justify-center mb-3">{String(i + 1).padStart(2, '0')}</div>
                <div className="text-white text-[13.5px] font-bold leading-snug">{p.t}</div>
                <p className="text-white/60 text-[12px] mt-1.5 leading-relaxed">{p.d}</p>
                <span className="inline-block mt-2.5 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wider">{p.tag}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-4 text-[12.5px] text-white/70 leading-relaxed">
            <strong className="text-red-300 font-mono-spec text-[10.5px] uppercase tracking-widest block mb-1">Le conducteur modèle</strong>
            {data.conduiteModele}
          </div>
        </div>

        {/* Comparatif STOP vs Cédez */}
        <div className="mt-8 rounded-2xl border border-blue-900/50 bg-gradient-to-br from-[#0e1526] to-[#0d0d0e] p-5 sm:p-7" data-testid="stopcedez-comparatif">
          <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2 mb-4"><Scale size={18} className="text-blue-300" /> STOP vs Cédez — le face-à-face</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[560px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="py-2.5 pr-4 text-white/45 text-[10px] uppercase tracking-widest font-mono-spec font-bold w-[28%]">Critère</th>
                  <th className="py-2.5 pr-4 text-red-300 text-[12px] font-black uppercase tracking-wider">🛑 STOP (AB4)</th>
                  <th className="py-2.5 text-blue-300 text-[12px] font-black uppercase tracking-wider">🔻 Cédez (AB3a)</th>
                </tr>
              </thead>
              <tbody>
                {COMPARATIF.map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-3 pr-4 text-white/55 text-[12px] font-semibold">{r.k}</td>
                    <td className="py-3 pr-4 text-white/85 text-[12.5px] leading-snug">{r.stop}</td>
                    <td className="py-3 text-white/85 text-[12.5px] leading-snug">{r.cedez}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* QCM */}
        <Quiz questions={data.quiz} storageKey={scen} />

        {/* Grille de révision */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-7" id="grille">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h3 className="font-serif-editorial text-white text-[20px] sm:text-[24px] inline-flex items-center gap-2">
              <ClipboardCheck size={18} className="text-red-400" /> Grille de révision à cocher
            </h3>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full border text-[12px] font-mono-spec font-bold ${nbChecked >= 8 ? 'bg-emerald-950/30 border-emerald-600/40 text-emerald-300' : 'bg-white/[0.04] border-white/10 text-white/70'}`} data-testid="stopcedez-grille-score">
                {nbChecked}/{data.grille.length} validés
              </span>
              <button type="button" onClick={() => setShowCorrection(!showCorrection)} data-testid="stopcedez-grille-correction"
                className="h-9 px-3 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
                <Eye size={13} /> {showCorrection ? 'Masquer' : 'Voir'} correction moniteur
              </button>
              <button type="button" onClick={reset} data-testid="stopcedez-grille-reset"
                className="h-9 px-3 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/70 text-[12px] font-semibold inline-flex items-center gap-1.5">
                <RotateCcw size={13} /> Réinitialiser
              </button>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {data.grille.map((g, i) => (
              <button key={i} type="button" onClick={() => toggle(i)} data-testid={`stopcedez-critere-${i}`}
                className="w-full flex items-center gap-3 py-3 text-left group">
                <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition ${checked[i] ? 'bg-emerald-500 border-emerald-500' : 'border-white/25 group-hover:border-white/50'}`}>
                  {checked[i] && <CheckCircle2 size={14} className="text-black" />}
                </span>
                <span className={`flex-1 text-[13.5px] leading-snug transition ${checked[i] ? 'text-white' : 'text-white/70'}`}>{g.l}</span>
                {showCorrection && <span className="hidden sm:block text-white/40 text-[11px] italic shrink-0">{g.c} · cocher si validé</span>}
              </button>
            ))}
          </div>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div className="rounded-xl bg-[#101b33] border border-blue-900/50 p-4">
              <div className="text-blue-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1">Seuil de validation</div>
              <p className="text-white/85 text-[12.5px] leading-snug">8/{data.grille.length} = compétence acquise en autonomie. En dessous = refaire avec verbalisation à voix haute.</p>
            </div>
            <div className="rounded-xl bg-amber-950/15 border border-amber-700/30 p-4">
              <div className="text-amber-300 text-[10px] uppercase tracking-widest font-mono-spec font-bold mb-1 inline-flex items-center gap-1.5"><Quote size={11} /> Phrase clé moniteur</div>
              <p className="text-white text-[13px] font-serif-editorial leading-snug">{data.phrase}</p>
            </div>
          </div>
        </div>

        {/* Check-list élève */}
        <ChecklistEleve />

        {/* Méthode RACD + checklist examen */}
        <div className="mt-8 grid lg:grid-cols-[1fr_1.3fr] gap-4">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#14090b] to-[#0d0d0e] p-5 sm:p-6">
            <div className="text-red-300 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-2">Méthode R.A.C.D — Intersections à priorité</div>
            <h3 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">Repérer, Adapter,<br />Contrôler, Décider.</h3>
            <p className="text-white/55 text-[12.5px] mt-2 leading-relaxed">Méthode à réciter à l'oral avant chaque stop ou cédez-le-passage.</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {METHODE.map((p, i) => (
                <div key={i} className="rounded-xl bg-white/[0.04] border border-white/10 p-3">
                  <div className="text-red-300 font-mono-spec text-[11px] font-bold uppercase tracking-wider">{p.k}</div>
                  <div className="text-white/65 text-[11.5px] mt-1 leading-snug">{p.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6">
            <div className="text-white/45 text-[10px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-3 inline-flex items-center gap-1.5"><ListChecks size={13} className="text-red-400" /> Check-list examen — stop & cédez</div>
            <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
              {CHECKLIST.map((c, i) => (
                <li key={i} className="text-[12.5px] text-white/70 leading-snug">
                  <strong className="text-white">{c.t}</strong> : {c.d}
                </li>
              ))}
            </ul>
            <div className="mt-4 inline-flex px-3 py-1.5 rounded-full bg-black/40 border border-white/10 text-white/55 font-mono-spec text-[10.5px]">Support : {data.badge}</div>
            <p className="text-white/35 text-[11px] mt-3">Fiche utilisable en salle, en voiture ou en autonomie · Sans impression · Code Voiture × moniteurs ECSR</p>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
